import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { BehaviorSubject } from 'rxjs';

import { UtilService } from './util.service';
import { AuthService } from './auth.service';
import { NbToastrService } from '@nebular/theme';
import { EnvService } from './env.service';
import { getAuthConfig } from '../../auth.config';

@Injectable({ providedIn: 'root' })
export class AuthSsoService {
  public isAuthenticated = new BehaviorSubject<boolean>(false);
  public userProfile = new BehaviorSubject<any>(null);

  // Variabel untuk menyimpan konfigurasi SSO yang aktif
  private currentAuthConfig: any;

  constructor(
    private oauthService: OAuthService,
    private router: Router,
    private zone: NgZone,
    private util: UtilService,
    private authService: AuthService,
    private tostr: NbToastrService,
    private envService: EnvService,
  ) {
    const activeEnv = this.envService.env;
    this.currentAuthConfig = getAuthConfig(activeEnv);

    this.oauthService.configure(this.currentAuthConfig);

    this.oauthService.loadDiscoveryDocument().then(() => {
      const hasToken = this.oauthService.hasValidAccessToken();
      this.isAuthenticated.next(hasToken);

      if (hasToken) {
        this.oauthService.setupAutomaticSilentRefresh();
        this.updateUserProfile();
      }
    });

    this.oauthService.events.subscribe((event) => {
      if (
        event instanceof OAuthErrorEvent &&
        event.type === 'token_refresh_error'
      ) {
        this.tostr.danger('Token tidak valid !');
        this.executeLocalCleanup();
      }
    });

    if (Capacitor.isNativePlatform()) {
      this.setupMobileDeepLink();
    }
  }

  public async login() {
    try {
      await this.oauthService.loadDiscoveryDocument();
      const url = await (this.oauthService as any).createLoginUrl();

      if (!url.startsWith('http')) {
        this.tostr.danger('URL SSO Gagal Terbentuk!');
        return;
      }

      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: url, windowName: '_self' });
      } else {
        this.oauthService.initCodeFlow();
      }
    } catch (err: any) {
      this.tostr.danger('Terjadi error saat mencoba membuat URL login', err);

      if (err.hasOwnProperty('status')) {
        this.tostr.danger('SSO Error Status:', err.status);
      } else {
        this.tostr.danger(
          'SSO Error Message:',
          err.message || JSON.stringify(err),
        );
      }
    }
  }

  private setupMobileDeepLink() {
    App.addListener('appUrlOpen', (event: any) => {
      if (event.url.startsWith('com.badungkab.sikapbadung://')) {
        Browser.close();

        const urlObj = new URL(event.url);
        const searchParams = urlObj.search;
        const pathname = urlObj.pathname;

        this.zone.run(() => {
          if (event.url.includes('/auth/callback')) {
            this.router.navigateByUrl('/auth/callback' + searchParams);
          } else {
            this.router.navigateByUrl('/public-monitor');
          }
        });
      }
    });
  }

  public async handlePostLogin() {
    const keycloakToken = this.oauthService.getAccessToken();

    if (!keycloakToken) return;

    this.loginSso(keycloakToken).subscribe({
      next: async (response: any) => {
        try {
          if (response && response.sanctum_token) {
            const sanctumToken = response.sanctum_token;

            if (Capacitor.isNativePlatform()) {
              await SecureStoragePlugin.set({
                key: 'sanctum_token',
                value: sanctumToken,
              });
              await SecureStoragePlugin.set({
                key: 'token',
                value: sanctumToken,
              });
            } else {
              localStorage.setItem('sanctum_token', sanctumToken);
              localStorage.setItem('token', sanctumToken);
            }

            const mappedData = {
              data: {
                user: response.user,
                pegawai: response.pegawai,
                sanctum_token: response.sanctum_token,
              },
            };
            await this.authService.storeUserData(mappedData);

            if (response.tahun) {
              localStorage.setItem('tahun_skp', JSON.stringify(response.tahun));
              this.authService.tahunSkpSubject.next(response.tahun);
            }

            this.zone.run(() => {
              this.router.navigate(['/app/dashboard'], { replaceUrl: true });
            });
          } else {
            this.tostr.warning(
              'Login gagal dari sisi server (Data token tidak ditemukan):',
              response,
            );
            this.executeLocalCleanup();
          }
        } catch (e) {
          this.tostr.danger('CRASH SAAT MEMPROSES DATA LOGIN:', e);
        }
      },
      error: (err) => {
        this.tostr.danger('Gagal menukar token dengan Server', err);
        this.executeLocalCleanup();
      },
    });
  }

  public logout() {
    this.completeLogout().subscribe({
      next: () => {
        this.executeLocalCleanup();
      },
      error: (err) => {
        this.tostr.warning('Gagal menghubungi server, mengakhiri sesi...', err);
        this.executeLocalCleanup();
      },
    });
  }

  public updateUserProfile(): void {
    const claims = this.oauthService.getIdentityClaims();
    if (claims) {
      this.userProfile.next(claims);
    }
  }

  private async executeLocalCleanup() {
    if (Capacitor.isNativePlatform()) {
      await SecureStoragePlugin.remove({ key: 'sanctum_token' }).catch(
        () => {},
      );
      await SecureStoragePlugin.remove({ key: 'token' }).catch(() => {});
    } else {
      localStorage.removeItem('sanctum_token');
      localStorage.removeItem('token');
    }

    this.isAuthenticated.next(false);
    this.userProfile.next(null);
    this.authService.clear();

    if (Capacitor.isNativePlatform()) {
      const refreshToken = this.oauthService.getRefreshToken();

      const logoutEndpoint = `${this.currentAuthConfig.issuer}/protocol/openid-connect/logout`;

      if (refreshToken) {
        try {
          const body = new URLSearchParams();
          body.set('client_id', this.currentAuthConfig.clientId);
          body.set('refresh_token', refreshToken);

          await fetch(logoutEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: body.toString(),
          });
        } catch (error) {
          console.error('Gagal menghapus sesi Keycloak di server:', error);
        }
      }

      this.oauthService.logOut(true);

      this.zone.run(() => {
        this.router.navigate(['/public-monitor']);
      });
    } else {
      this.oauthService.revokeTokenAndLogout();
    }
  }

  private loginSso(keycloakToken) {
    return this.util.apiPost('auth/login-sso', {
      keycloak_token: keycloakToken,
    });
  }

  private completeLogout() {
    return this.util.apiPost('auth/logout', {});
  }

  public async forceLocalCleanupOnly() {
    if (Capacitor.isNativePlatform()) {
      await SecureStoragePlugin.remove({ key: 'sanctum_token' }).catch(
        () => {},
      );
      await SecureStoragePlugin.remove({ key: 'token' }).catch(() => {});
    } else {
      localStorage.removeItem('sanctum_token');
      localStorage.removeItem('token');
    }

    this.oauthService.logOut(true);

    this.isAuthenticated.next(false);
    this.userProfile.next(null);
    this.authService.clear();
  }
}
